// NotificationContext — global state for the in-app notification system.
//
// Wraps a long-lived WebSocket connection (one per logged-in user) plus the
// REST API for list/read/preferences. Re-exposes the freshest unread count
// + recent notifications so any header bell + dropdown + center page can
// share state without prop drilling.
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { useAuth } from './AuthContext';
import { auth as firebaseAuth } from '../lib/firebase';
import { toast } from 'sonner';
import {
  enablePush,
  disablePush,
  subscribeForeground,
  pushPermissionStatus,
} from '../lib/firebaseMessaging';
import { isNative } from '../lib/native';
import {
  enableNativePush,
  disableNativePush,
  deepLinkFromNotification,
  nativePushPermission,
} from '../lib/nativePush';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationProvider>');
  return ctx;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrlFor(userId, token) {
  // Convert https → wss / http → ws
  const u = new URL(`${BACKEND_URL}/api/ws/notifications/${userId}`);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.searchParams.set('token', token);
  return u.toString();
}

export const NotificationProvider = ({ children }) => {
  const { user, firebaseUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);   // recent notifications (max 50 in memory)
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get('/notifications', { params: { limit: 50 } });
      setItems(res.data.items || []);
      setUnread(res.data.unread || 0);
    } catch (e) {
      // Silent fail — header bell won't crash.
      console.warn('Notification refresh failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial refresh + on user change
  useEffect(() => {
    if (user) refresh();
    else { setItems([]); setUnread(0); }
  }, [user, refresh]);

  // WebSocket connection — bound to firebaseUser so we have a guaranteed-fresh ID token
  useEffect(() => {
    if (!user || !firebaseUser) return;
    let cancelled = false;

    const connect = async () => {
      try {
        const token = await firebaseAuth.currentUser?.getIdToken();
        if (!token || cancelled) return;
        const url = wsUrlFor(user.id, token);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onmessage = (ev) => {
          let data; try { data = JSON.parse(ev.data); } catch { return; }
          if (data?.type === 'notification') {
            setItems((prev) => [data, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
            // Lightweight in-app toast — non-blocking
            toast.message(data.title, { description: data.body });
          }
        };

        ws.onclose = () => {
          if (cancelled) return;
          // Auto-reconnect with mild backoff
          reconnectRef.current = setTimeout(connect, 5000);
        };
        ws.onerror = () => {
          // onclose will follow — let it handle reconnect.
        };
      } catch (e) {
        if (!cancelled) reconnectRef.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [user, firebaseUser]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await axiosInstance.post(`/notifications/${id}/read`); }
    catch (e) { /* optimistic — don't roll back */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
    setUnread(0);
    try { await axiosInstance.post('/notifications/read-all'); }
    catch (e) { /* optimistic */ }
  }, []);

  // ─── Push (FCM web push) ───────────────────────────────────────────────────
  const [pushPermission, setPushPermission] = useState('default'); // 'granted'|'denied'|'default'|'unsupported'

  const refreshPushPermission = useCallback(async () => {
    // In the apps, ask the phone — the browser Notification API isn't there.
    const p = isNative() ? await nativePushPermission() : await pushPermissionStatus();
    setPushPermission(p);
    return p;
  }, []);

  useEffect(() => { refreshPushPermission(); }, [refreshPushPermission]);

  // Auto-register an existing push token whenever a logged-in user lands on
  // the app and they've previously granted permission. This refreshes the
  // FCM token if Firebase rotated it. Native iOS/Android: route to the
  // Capacitor push bridge instead.
  useEffect(() => {
    if (!user) return;
    (async () => {
      if (isNative()) {
        await enableNativePush({
          onTap: (notification) => {
            const link = deepLinkFromNotification(notification);
            if (link) navigate(link);
          },
          onForeground: (n) => {
            const title = n?.title || n?.data?.title;
            const body  = n?.body  || n?.data?.body;
            if (title) toast.message(title, { description: body });
          },
        });
        return;
      }
      const p = await pushPermissionStatus();
      if (p === 'granted' && localStorage.getItem('byard-push-enabled') === '1') {
        // Silent re-registration — this also calls /push/tokens which is idempotent.
        await enablePush();
      }
    })();
  }, [user, navigate]);

  // Foreground push handler (web only) — show toast. Native foreground is
  // wired inside enableNativePush().
  useEffect(() => {
    if (!user || isNative()) return;
    let unsub = () => {};
    (async () => {
      unsub = await subscribeForeground((payload) => {
        const title = payload?.notification?.title || payload?.data?.title;
        const body  = payload?.notification?.body  || payload?.data?.body;
        if (title) toast.message(title, { description: body });
      });
    })();
    return () => { try { unsub(); } catch {} };
  }, [user]);

  const enablePushAndRefresh = useCallback(async () => {
    if (isNative()) {
      const r = await enableNativePush({
        onTap: (n) => {
          const link = deepLinkFromNotification(n);
          if (link) navigate(link);
        },
        onForeground: (n) => {
          const title = n?.title || n?.data?.title;
          const body  = n?.body  || n?.data?.body;
          if (title) toast.message(title, { description: body });
        },
      });
      await refreshPushPermission();
      return r;
    }
    const r = await enablePush();
    setPushPermission(r.status === 'granted' ? 'granted' : (r.status || 'default'));
    return r;
  }, [navigate, refreshPushPermission]);

  const disablePushAndRefresh = useCallback(async () => {
    if (isNative()) {
      await disableNativePush();
    } else {
      await disablePush();
    }
    await refreshPushPermission();
  }, [refreshPushPermission]);

  const value = {
    items, unread, loading, refresh, markRead, markAllRead,
    pushPermission, enablePush: enablePushAndRefresh, disablePush: disablePushAndRefresh,
  };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
